/**
 * Sign-in → app handoff (#5559): when the veil covers the page, as pure
 * functions of the route and the handoff state, so every sign-in path
 * (soft form submits, the Google return, Electron's document load) follows
 * one rule.
 */

export const SIGNING_IN_PATH = '/auth/signing-in';

/** A soft sign-in that never reaches `/auth/signing-in` stops covering. */
export const AUTH_HANDOFF_PENDING_MAX_MS = 8_000;
/**
 * No handoff covers longer than this, on any route, `/auth/signing-in`
 * included: past it the veil yields to whatever is underneath, and
 * `/auth/signing-in` shows its recovery (retry, back to sign in).
 */
export const AUTH_HANDOFF_MAX_MS = 25_000;

export type AuthHandoffState = {
  /** A handoff is running: started by a sign-in or by `/auth/signing-in`. */
  active: boolean;
  /** `/auth/signing-in` has rendered during this handoff. */
  reachedSigningIn: boolean;
  /** Full-screen loaders and pending workspace shells mounted in this handoff. */
  holds: number;
  /**
   * The handoff gave the screen back (deadline, or a screen that needs the
   * user). Stays set after the handoff ends, so `/auth/signing-in` keeps its
   * recovery visible, until the next handoff starts.
   */
  yielded: boolean;
  /** Bumped whenever a handoff starts or ends; holds belong to one. */
  generation: number;
};

export const IDLE_AUTH_HANDOFF: AuthHandoffState = {
  active: false,
  reachedSigningIn: false,
  holds: 0,
  yielded: false,
  generation: 0,
};

export type AuthHandoffCover = 'cover' | 'release' | 'idle';

export function isSigningInPath(pathname: string | null | undefined) {
  return pathname === SIGNING_IN_PATH || pathname === `${SIGNING_IN_PATH}/`;
}

/**
 * Routes a handoff passes through without painting the app: `/dashboard` is
 * the default post-sign-in target and only server-redirects to the workspace
 * (or holds a loader while it decides). Its redirect leaves the page empty
 * while the workspace loads, so the veil stays over it.
 */
const HANDOFF_TRANSIT_PATHS = new Set(['/dashboard', '/dashboard/']);

export function isHandoffTransitPath(pathname: string | null | undefined) {
  return !!pathname && HANDOFF_TRANSIT_PATHS.has(pathname);
}

/**
 * `cover`: the veil must be on screen. `release`: the destination has
 * painted and nothing is loading, so the veil can lift. `idle`: no handoff.
 */
export function resolveAuthHandoffCover(
  pathname: string | null | undefined,
  state: AuthHandoffState
): AuthHandoffCover {
  if (state.yielded) return state.active ? 'release' : 'idle';
  // Server-rendered too: the Google return and Electron's loadURL land here.
  if (isSigningInPath(pathname)) return 'cover';
  if (!state.active) return 'idle';
  // A form submitted and is navigating to /auth/signing-in.
  if (!state.reachedSigningIn) return 'cover';
  if (state.holds > 0 || isHandoffTransitPath(pathname)) return 'cover';
  return 'release';
}
